import { ReactNode, useMemo, useRef, useState } from 'react';

import { LogList } from '@@/LogViewer/LogList/LogList';
import { LogController } from '@@/LogViewer/LogController/LogController';
import { GetLogsFnType } from '@@/LogViewer/types';
import { Widget } from '@@/Widget';

import { useSearchStatus } from './hooks/useSearchStatus';
import { useSearchLogs } from './hooks/useSearchLogs';
import { useFilterLogs } from './hooks/useFilterLogs';
import { useLogsQuery } from './hooks/useLogsQuery';
import { useControllerState } from './hooks/useControllerStates';
import { LogViewerContext, LogViewerProvider } from './context';

type RendererWithContextFunc = (ctx: LogViewerContext) => ReactNode;

interface Props {
  getLogsFn: GetLogsFnType;
  hideFetch?: boolean;
  hideLines?: boolean;
  hideTimestamp?: boolean;
  useTimeOptions?: boolean;
  resourceName: string;
  resourceType: string;
  minDate?: Date;
  renderHeader?: RendererWithContextFunc;
  renderAlternateBody?: RendererWithContextFunc;
}

export function LogViewer({
  getLogsFn,
  hideFetch,
  hideLines,
  hideTimestamp,
  useTimeOptions,
  resourceName,
  resourceType,
  minDate,
  renderHeader,
  renderAlternateBody,
}: Props) {
  const logViewerRef = useRef<HTMLDivElement>(null);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);

  const [controllerState, setControllerValue] = useControllerState();
  const { logsQuery, logs } = useLogsQuery(
    getLogsFn,
    controllerState,
    resourceType,
    resourceName
  );

  const { searchedLogs } = useSearchLogs(logs, controllerState.keyword);

  const { filteredLogs } = useFilterLogs(
    searchedLogs,
    controllerState.filter,
    controllerState.keyword
  );

  const { searchStatus } = useSearchStatus(filteredLogs, visibleStartIndex);

  const context = useMemo<LogViewerContext>(
    () => ({
      controllerState,
      setControllerValue,
      logViewerRef,
      logs: filteredLogs,
      visibleStartIndex,
      setVisibleStartIndex,
      searchStatus,
      hideFetch,
      hideLines,
      hideTimestamp,
      resourceName,
      logsQuery,
    }),
    [
      controllerState,
      filteredLogs,
      hideFetch,
      hideLines,
      hideTimestamp,
      logsQuery,
      resourceName,
      searchStatus,
      setControllerValue,
      visibleStartIndex,
    ]
  );

  const Header = (!!renderHeader && renderHeader(context)) || null;
  const Body = (!!renderAlternateBody && renderAlternateBody(context)) || (
    <LogList logs={filteredLogs} />
  );

  return (
    <LogViewerProvider value={context}>
      <div className="h-full">
        <Widget
          className="h-[calc(100%-20px)] mx-4 flex flex-col"
          mRef={logViewerRef}
        >
          <LogController minDate={minDate} useTimeOptions={useTimeOptions}>
            {Header}
          </LogController>
          <Widget.Body
            className="no-padding flex-auto"
            contentClassName="h-full"
          >
            {Body}
          </Widget.Body>
        </Widget>
      </div>
    </LogViewerProvider>
  );
}
